import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { produce } from 'immer';

/**
 * Application Store
 *
 * Manages application state including:
 * - Dashboard data (bio, ref, olevel, result, pay, photo status)
 * - Current application data
 * - Application progress tracking
 * - Loading and error states
 *
 * @typedef {Object} DashboardData
 * @property {boolean} bio - Bio data completion status
 * @property {boolean} ref - Referee information completion status
 * @property {boolean} olevel - O'level results completion status
 * @property {boolean} result - Result submission status
 * @property {boolean} pay - Payment status
 * @property {boolean} photo - Photo upload status
 *
 * @typedef {Object} ApplicationData
 * @property {string|null} id - Application ID
 * @property {string|null} status - Application status (pending, submitted, approved, rejected)
 * @property {Object|null} bioData - Biographical data
 * @property {Object|null} refereeData - Referee information
 * @property {Array} olevelResults - O'level results
 * @property {Object|null} paymentData - Payment information
 * @property {string|null} photoUrl - Uploaded photo URL
 * @property {number} progressPercentage - Overall completion percentage (0-100)
 * @property {string|null} submittedAt - Submission timestamp
 * @property {string|null} lastUpdatedAt - Last update timestamp
 */

const useApplicationStore = create(
  persist(
    (set, get) => ({
      // Dashboard status
      dashboard: {
        bio: false,
        ref: false,
        olevel: false,
        result: false,
        pay: false,
        photo: false,
      },

      // Current application data
      application: {
        id: null,
        status: null,
        bioData: null,
        refereeData: null,
        olevelResults: [],
        paymentData: null,
        photoUrl: null,
        progressPercentage: 0,
        submittedAt: null,
        lastUpdatedAt: null,
      },

      // Loading states
      isLoading: false,
      isDashboardLoading: false,
      isSubmitting: false,

      // Error states
      error: null,
      dashboardError: null,

      /**
       * Set dashboard data
       * @param {Partial<DashboardData>} dashboardData - Dashboard status updates
       */
      setDashboard: (dashboardData) =>
        set(
          produce((state) => {
            state.dashboard = { ...state.dashboard, ...dashboardData };
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Update a specific dashboard field
       * @param {keyof DashboardData} field - Field name to update
       * @param {boolean} value - New value
       */
      updateDashboardField: (field, value) =>
        set(
          produce((state) => {
            state.dashboard[field] = value;
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Set application data
       * @param {Partial<ApplicationData>} applicationData - Application data to update
       */
      setApplication: (applicationData) =>
        set(
          produce((state) => {
            state.application = { ...state.application, ...applicationData };
            state.application.lastUpdatedAt = new Date().toISOString();
          })
        ),

      /**
       * Update bio data
       * @param {Object} bioData - Biographical data
       */
      updateBioData: (bioData) =>
        set(
          produce((state) => {
            state.application.bioData = bioData;
            state.dashboard.bio = true;
            state.application.lastUpdatedAt = new Date().toISOString();
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Update referee data
       * @param {Object} refereeData - Referee information
       */
      updateRefereeData: (refereeData) =>
        set(
          produce((state) => {
            state.application.refereeData = refereeData;
            state.dashboard.ref = true;
            state.application.lastUpdatedAt = new Date().toISOString();
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Update O'level results
       * @param {Array} olevelResults - O'level examination results
       */
      updateOlevelResults: (olevelResults) =>
        set(
          produce((state) => {
            state.application.olevelResults = olevelResults;
            state.dashboard.olevel = olevelResults.length > 0;
            state.application.lastUpdatedAt = new Date().toISOString();
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Update payment data
       * @param {Object} paymentData - Payment information
       */
      updatePaymentData: (paymentData) =>
        set(
          produce((state) => {
            state.application.paymentData = paymentData;
            state.dashboard.pay = true;
            state.application.lastUpdatedAt = new Date().toISOString();
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Update photo
       * @param {string} photoUrl - Photo URL
       */
      updatePhoto: (photoUrl) =>
        set(
          produce((state) => {
            state.application.photoUrl = photoUrl;
            state.dashboard.photo = true;
            state.application.lastUpdatedAt = new Date().toISOString();
            state.application.progressPercentage = get().calculateProgress();
          })
        ),

      /**
       * Calculate overall progress percentage based on completed sections
       * @returns {number} Progress percentage (0-100)
       */
      calculateProgress: () => {
        const dashboard = get().dashboard;
        const fields = Object.values(dashboard);
        const completed = fields.filter(Boolean).length;
        return Math.round((completed / fields.length) * 100);
      },

      /**
       * Fetch dashboard data from API
       * @param {string} applicantId - Applicant ID
       * @returns {Promise<void>}
       */
      fetchDashboard: async (applicantId) => {
        set({ isDashboardLoading: true, dashboardError: null });
        try {
          // API call would go here
          // const response = await api.get(`/dashboard/${applicantId}`);
          // set({ dashboard: response.data, isDashboardLoading: false });

          // Placeholder - replace with actual API call
          set({ isDashboardLoading: false });
        } catch (error) {
          set({
            dashboardError: error.message || 'Failed to fetch dashboard data',
            isDashboardLoading: false,
          });
        }
      },

      /**
       * Fetch full application data from API
       * @param {string} applicationId - Application ID
       * @returns {Promise<void>}
       */
      fetchApplication: async (applicationId) => {
        set({ isLoading: true, error: null });
        try {
          // API call would go here
          // const response = await api.get(`/applications/${applicationId}`);
          // set({ application: response.data, isLoading: false });

          // Placeholder - replace with actual API call
          set({ isLoading: false });
        } catch (error) {
          set({
            error: error.message || 'Failed to fetch application data',
            isLoading: false,
          });
        }
      },

      /**
       * Submit application
       * @returns {Promise<boolean>} Success status
       */
      submitApplication: async () => {
        set({ isSubmitting: true, error: null });
        try {
          const application = get().application;

          // API call would go here
          // const response = await api.post('/applications/submit', application);

          set(
            produce((state) => {
              state.application.status = 'submitted';
              state.application.submittedAt = new Date().toISOString();
              state.isSubmitting = false;
            })
          );

          return true;
        } catch (error) {
          set({
            error: error.message || 'Failed to submit application',
            isSubmitting: false,
          });
          return false;
        }
      },

      /**
       * Reset application to initial state
       */
      resetApplication: () =>
        set(
          produce((state) => {
            state.dashboard = {
              bio: false,
              ref: false,
              olevel: false,
              result: false,
              pay: false,
              photo: false,
            };
            state.application = {
              id: null,
              status: null,
              bioData: null,
              refereeData: null,
              olevelResults: [],
              paymentData: null,
              photoUrl: null,
              progressPercentage: 0,
              submittedAt: null,
              lastUpdatedAt: null,
            };
            state.error = null;
            state.dashboardError = null;
          })
        ),

      /**
       * Clear errors
       */
      clearErrors: () =>
        set({ error: null, dashboardError: null }),

      /**
       * Get dashboard completion status
       * @returns {DashboardData}
       */
      getDashboard: () => get().dashboard,

      /**
       * Get application data
       * @returns {ApplicationData}
       */
      getApplication: () => get().application,

      /**
       * Check if application is complete and ready for submission
       * @returns {boolean}
       */
      isApplicationComplete: () => {
        const dashboard = get().dashboard;
        return Object.values(dashboard).every(Boolean);
      },

      /**
       * Get missing sections
       * @returns {string[]} Array of incomplete section names
       */
      getMissingSections: () => {
        const dashboard = get().dashboard;
        return Object.entries(dashboard)
          .filter(([_, completed]) => !completed)
          .map(([section]) => section);
      },
    }),
    {
      name: 'application-storage',
      partialize: (state) => ({
        dashboard: state.dashboard,
        application: state.application,
      }),
    }
  )
);

export default useApplicationStore;
